import { useAtomValue } from 'jotai'
import { type ComplexNumericMode, WidgetInputComplexNumericField, WidgetLabel } from '../../../../rapid-ui'
import { layerTreeAtom } from '../../atoms'
import { useCanvasStore } from '../../canvas-store'
import type { Dimension, DimensionMode, FloatAnchor, LeafLayer } from '../../data/v1'
import { useDesignerActions } from '../../use-designer-actions'
import { useLayerTreeActions } from '../../use-layer-tree-actions'

const dimensionModes: ComplexNumericMode<DimensionMode>[] = [
	{ id: 'px', label: 'px', numeric: true },
	{ id: '%', label: '%', numeric: true },
]

function DimensionField(
	{ label, value, onChange, onTransientChange }: {
		label: string
		value: Dimension
		onChange: (d: Dimension) => void
		onTransientChange?: (d: Dimension) => void
	},
) {
	const numericValue = value.mode === '%' ? value.percentValue : value.pxValue
	const key = value.mode === '%' ? 'percentValue' : 'pxValue'

	return (
		<WidgetLabel label={label}>
			<WidgetInputComplexNumericField
				value={numericValue}
				onChange={(v) => onChange({ ...value, [key]: v })}
				onTransientChange={onTransientChange ? (v) => onTransientChange({ ...value, [key]: v }) : undefined}
				defaultValue={0}
				modes={dimensionModes}
				mode={value.mode}
				onModeChange={(mode) => onChange({ ...value, mode })}
			/>
		</WidgetLabel>
	)
}

const ANCHOR_OPTIONS: FloatAnchor[] = [
	'top-left',
	'top-center',
	'top-right',
	'center-left',
	'center',
	'center-right',
	'bottom-left',
	'bottom-center',
	'bottom-right',
]

export function AnchorGrid({ value, onChange }: { value: FloatAnchor; onChange: (a: FloatAnchor) => void }) {
	return (
		<WidgetLabel label="Anchor">
			<div className="grid grid-cols-3 gap-0.5">
				{ANCHOR_OPTIONS.map((anchor) => (
					<button
						key={anchor}
						type="button"
						onClick={() => onChange(anchor)}
						className={`flex size-5 items-center justify-center rounded transition-colors ${
							value === anchor
								? 'bg-blue-500 ring-1 ring-blue-500'
								: 'bg-grey-100 hover:bg-grey-200'
						}`}
					>
						<span
							className={`size-1.5 rounded-full ${value === anchor ? 'bg-white' : 'bg-grey-400'}`}
						/>
					</button>
				))}
			</div>
		</WidgetLabel>
	)
}

export function FloatControls({ layer }: { layer: LeafLayer }) {
	const store = useCanvasStore()
	const layerTree = useAtomValue(layerTreeAtom, { store })
	const isRoot = layerTree.some(node => node.id === layer.id)
	const { updateLayer } = useDesignerActions()
	const { moveToRoot } = useLayerTreeActions()

	if (!isRoot) {
		return (
			<div className="flex items-center gap-1 text-xs text-grey-400 mb-4">
				<button
					type="button"
					onClick={() => moveToRoot(layer.id)}
					className="rounded bg-grey-50 px-2 py-1 font-medium text-grey-600 hover:bg-grey-100"
				>
					Move to root
				</button>
				<div>
					to float this layer.
				</div>
			</div>
		)
	}

	return (
		<div className="flex flex-col gap-2 bg-grey-50 p-0.5 ring ring-grey-100 mb-4 rounded-md">
			<div className="p-2 bg-white ring ring-grey-100 rounded-sm">
				<label className="flex items-center gap-2 mb-2 text-center">
					<span className="text-[10px] font-medium tracking-wider text-grey-400 uppercase">
						This layer is floating because it's a root layer.
					</span>
				</label>

				{layer.floating && (
					<div className="flex flex-col gap-4">
						<AnchorGrid
							value={layer.floatAnchor}
							onChange={(floatAnchor) => updateLayer(layer.id, { floatAnchor })}
						/>
						<DimensionField
							label="X"
							value={layer.floatX}
							onChange={(floatX) => updateLayer(layer.id, { floatX })}
							onTransientChange={(floatX) => updateLayer(layer.id, { floatX }, { transient: true })}
						/>
						<DimensionField
							label="Y"
							value={layer.floatY}
							onChange={(floatY) => updateLayer(layer.id, { floatY })}
							onTransientChange={(floatY) => updateLayer(layer.id, { floatY }, { transient: true })}
						/>
					</div>
				)}
			</div>
		</div>
	)
}
